import type { AppConfig } from '@/types/config'

export type OnboardingArtKind =
  | 'welcome'
  | 'home'
  | 'library'
  | 'speed'
  | 'sleep'
  | 'subscribe'
  | 'scripture'
  | 'daily'
  | 'books'
  | 'video'
  | 'favourites'

export type OnboardingMotif = 'none' | 'silk' | 'rays' | 'shelf'

export interface OnboardingSlide {
  id: string
  art: OnboardingArtKind
  showSubscribe?: boolean
  title?: string
  message?: string
}

export interface OnboardingFlavour {
  glow: string
  brand: string
  backgroundEnd: string
  motif: OnboardingMotif
  /** White wordmark. Falls back to the app logo. */
  logo?: string
  logoHeight: number
}

export type OnboardingFinish = 'listen' | 'watch' | 'read'

const HEARD: OnboardingSlide[] = [
  { id: 'heardWelcome', art: 'welcome' },
  { id: 'heardHome', art: 'home' },
  { id: 'heardLibrary', art: 'library' },
  { id: 'heardPace', art: 'speed' },
  { id: 'heardSleep', art: 'sleep' },
  { id: 'heardSubscribe', art: 'subscribe', showSubscribe: true },
]

const LAMOUR: OnboardingSlide[] = [
  { id: 'lamourWelcome', art: 'welcome' },
  { id: 'lamourDiscover', art: 'home' },
  { id: 'lamourLibrary', art: 'library' },
  { id: 'lamourPace', art: 'speed' },
  { id: 'lamourSleep', art: 'sleep' },
  { id: 'lamourSubscribe', art: 'subscribe', showSubscribe: true },
]

const BIBLE: OnboardingSlide[] = [
  { id: 'bibleWelcome', art: 'welcome' },
  { id: 'bibleToday', art: 'daily' },
  { id: 'bibleScripture', art: 'scripture' },
  { id: 'biblePlace', art: 'library' },
  { id: 'bibleRest', art: 'sleep' },
  { id: 'bibleSubscribe', art: 'subscribe', showSubscribe: true },
]

const BOOKSHELF: OnboardingSlide[] = [
  { id: 'shelfWelcome', art: 'welcome' },
  { id: 'shelfBrowse', art: 'books' },
  { id: 'shelfLibrary', art: 'library' },
  { id: 'shelfPace', art: 'speed' },
  { id: 'shelfSleep', art: 'sleep' },
  { id: 'shelfSubscribe', art: 'subscribe', showSubscribe: true },
]

const SEEN: OnboardingSlide[] = [
  { id: 'seenWelcome', art: 'welcome' },
  { id: 'seenBrowse', art: 'video' },
  { id: 'seenFavourites', art: 'favourites' },
  { id: 'seenContinue', art: 'home' },
  { id: 'seenSubscribe', art: 'subscribe', showSubscribe: true },
]

const MX_BOOKS: OnboardingSlide[] = [
  { id: 'mxWelcome', art: 'welcome' },
  { id: 'mxShelf', art: 'books' },
  { id: 'mxLibrary', art: 'favourites' },
  { id: 'mxPace', art: 'books' },
  { id: 'mxSleep', art: 'library' },
  { id: 'mxSubscribe', art: 'subscribe', showSubscribe: true },
]

const MOTORSPORT: OnboardingSlide[] = [
  { id: 'motorWelcome', art: 'welcome' },
  { id: 'motorBrowse', art: 'home' },
  { id: 'motorLibrary', art: 'library' },
  { id: 'motorSubscribe', art: 'subscribe', showSubscribe: true },
]

const SLIDES: Record<string, OnboardingSlide[]> = {
  heard: HEARD,
  lamour: LAMOUR,
  bible: BIBLE,
  'reading-plus': BOOKSHELF,
  seen: SEEN,
  'mx-books': MX_BOOKS,
  motorsport: MOTORSPORT,
}

const FLAVOURS: Record<string, OnboardingFlavour> = {
  heard: {
    glow: '#C98BFF',
    brand: '#7209B7',
    backgroundEnd: '#3E0466',
    motif: 'none',
    logo: '/assets/images/heard/logo_dark.svg',
    logoHeight: 59,
  },
  lamour: {
    glow: '#F6C98E',
    brand: '#8E6A8D',
    backgroundEnd: '#4A2F4C',
    motif: 'silk',
    logoHeight: 59,
  },
  bible: {
    glow: '#F2CC8F',
    brand: '#3D405B',
    backgroundEnd: '#202236',
    motif: 'rays',
    logoHeight: 59,
  },
  'reading-plus': {
    glow: '#E9B872',
    brand: '#562E10',
    backgroundEnd: '#2C1606',
    motif: 'shelf',
    logoHeight: 59,
  },
  seen: {
    glow: '#FFE0C7',
    brand: '#FF6B35',
    backgroundEnd: '#E74C3C',
    motif: 'none',
    logoHeight: 82,
  },
  'mx-books': {
    glow: '#E7B4B8',
    brand: '#914349',
    backgroundEnd: '#3D1C20',
    motif: 'shelf',
    logoHeight: 59,
  },
  motorsport: {
    glow: '#F0A0AE',
    brand: '#C41E3A',
    backgroundEnd: '#4A0C16',
    motif: 'none',
    logoHeight: 59,
  },
}

export function onboardingSlidesForApp(appId: string): OnboardingSlide[] {
  return SLIDES[appId] ?? HEARD
}

export function onboardingFlavourForApp(appId: string): OnboardingFlavour {
  return FLAVOURS[appId] ?? FLAVOURS.heard
}

export function onboardingFinishFor(config: AppConfig): OnboardingFinish {
  if (config.features.video && !config.features.epubReader && !config.features.pdfReader) {
    return 'watch'
  }
  if ((config.features.epubReader || config.features.pdfReader) && !config.features.player) {
    return 'read'
  }
  return 'listen'
}

const PENDING_PREFIX = 'onboarding_pending_'
const DONE_PREFIX = 'onboarding_done_'
export const REDIRECT_AFTER_ONBOARDING = 'redirectAfterOnboarding'

const TOUR_EXEMPT_ROUTES = new Set([
  'onboarding',
  'terms',
  'privacy-policy',
  'subscribe',
  'login',
  'register',
])

export function markOnboardingPending(appId: string): void {
  localStorage.setItem(`${PENDING_PREFIX}${appId}`, '1')
}

export function isOnboardingPending(appId: string): boolean {
  return localStorage.getItem(`${PENDING_PREFIX}${appId}`) === '1'
    && localStorage.getItem(`${DONE_PREFIX}${appId}`) !== '1'
}

export function completeOnboarding(appId: string): void {
  localStorage.setItem(`${DONE_PREFIX}${appId}`, '1')
  localStorage.removeItem(`${PENDING_PREFIX}${appId}`)
}

export function shouldResumeOnboarding(
  routeName: string | symbol | null | undefined,
  appId: string,
): boolean {
  if (!isOnboardingPending(appId)) return false
  return !TOUR_EXEMPT_ROUTES.has(String(routeName ?? ''))
}

export function takeRedirectAfterOnboarding(): string | null {
  const next = sessionStorage.getItem(REDIRECT_AFTER_ONBOARDING)
  sessionStorage.removeItem(REDIRECT_AFTER_ONBOARDING)
  if (!next || next === '/' || next.startsWith('/onboarding')) return null
  return next
}
